/**
 * Learning API Test Page
 * Simple test page to verify all backend learning APIs are working correctly
 */

import React, { useState } from 'react';
import axios from 'axios';

import { API_BASE_URL } from '../config';
const API_BASE = `${API_BASE_URL}/v1/learning`;

interface TestResult {
    endpoint: string;
    method: string;
    status: 'pending' | 'success' | 'error';
    response?: any;
    error?: string;
    duration?: number;
}

const LearningAPITestPage: React.FC = () => {
    const [courseId, setCourseId] = useState('51a056c3-b29d-4741-9cb9-5645d7f08b82'); // Default course ID
    const [results, setResults] = useState<TestResult[]>([]);
    const [testing, setTesting] = useState(false);

    const updateResult = (endpoint: string, updates: Partial<TestResult>) => {
        setResults((prev) =>
            prev.map((r) => (r.endpoint === endpoint ? { ...r, ...updates } : r))
        );
    };

    const testEndpoint = async (
        endpoint: string,
        method: string,
        url: string,
        data?: any
    ): Promise<void> => {
        const startTime = Date.now();
        updateResult(endpoint, { status: 'pending' });

        try {
            let response;
            if (method === 'GET') {
                response = await axios.get(url);
            } else if (method === 'POST') {
                response = await axios.post(url, data);
            } else if (method === 'PUT') {
                response = await axios.put(url, data);
            } else if (method === 'DELETE') {
                response = await axios.delete(url);
            }

            const duration = Date.now() - startTime;
            updateResult(endpoint, {
                status: 'success',
                response: response?.data,
                duration,
            });
        } catch (error: any) {
            const duration = Date.now() - startTime;
            updateResult(endpoint, {
                status: 'error',
                error: error.response?.data?.message || error.message,
                duration,
            });
        }
    };

    const runAllTests = async () => {
        setTesting(true);
        setResults([
            { endpoint: 'GET /courses/:id/progress', method: 'GET', status: 'pending' },
            { endpoint: 'POST /courses/:id/progress', method: 'POST', status: 'pending' },
            { endpoint: 'GET /courses/:id/vocabulary', method: 'GET', status: 'pending' },
            { endpoint: 'GET /courses/:id/vocabulary/review-queue', method: 'GET', status: 'pending' },
            { endpoint: 'GET /courses/:id/vocabulary/stats', method: 'GET', status: 'pending' },
            { endpoint: 'GET /courses/:id/notes', method: 'GET', status: 'pending' },
            { endpoint: 'POST /notes', method: 'POST', status: 'pending' },
        ]);

        // Test 1: Get Progress
        await testEndpoint(
            'GET /courses/:id/progress',
            'GET',
            `${API_BASE}/courses/${courseId}/progress`
        );

        // Test 2: Update Progress
        await testEndpoint(
            'POST /courses/:id/progress',
            'POST',
            `${API_BASE}/courses/${courseId}/progress`,
            {
                mode: 'full_text',
                data: {
                    fullTextCompleted: true,
                    watchTimeSeconds: 120,
                },
            }
        );

        // Test 3: Get Vocabulary
        await testEndpoint(
            'GET /courses/:id/vocabulary',
            'GET',
            `${API_BASE}/courses/${courseId}/vocabulary`
        );

        // Test 4: Get Review Queue
        await testEndpoint(
            'GET /courses/:id/vocabulary/review-queue',
            'GET',
            `${API_BASE}/courses/${courseId}/vocabulary/review-queue?limit=5`
        );

        // Test 5: Get Vocabulary Stats
        await testEndpoint(
            'GET /courses/:id/vocabulary/stats',
            'GET',
            `${API_BASE}/courses/${courseId}/vocabulary/stats`
        );

        // Test 6: Get Notes
        await testEndpoint(
            'GET /courses/:id/notes',
            'GET',
            `${API_BASE}/courses/${courseId}/notes`
        );

        // Test 7: Create Note
        await testEndpoint(
            'POST /notes',
            'POST',
            `${API_BASE}/notes`,
            {
                courseId,
                content: 'Test note from API test page',
                timestamp: 45.5,
                tags: ['test', 'api'],
            }
        );

        setTesting(false);
    };

    const getStatusEmoji = (status: TestResult['status']) => {
        switch (status) {
            case 'pending':
                return '⏳';
            case 'success':
                return '✅';
            case 'error':
                return '❌';
        }
    };

    const successCount = results.filter((r) => r.status === 'success').length;
    const errorCount = results.filter((r) => r.status === 'error').length;

    return (
        <div className="min-h-screen bg-gray-50 p-8">
            <div className="max-w-6xl mx-auto">
                <div className="bg-white rounded-lg shadow-lg p-8">
                    <h1 className="text-3xl font-bold mb-6 text-gray-800">
                        🧪 Learning API Test Suite
                    </h1>

                    <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded">
                        <p className="text-sm text-blue-800">
                            <strong>Purpose:</strong> Test all Phase 1 learning API endpoints to
                            verify backend services are working correctly.
                        </p>
                    </div>

                    {/* Course ID Input */}
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Course ID (from published draft)
                        </label>
                        <input
                            type="text"
                            value={courseId}
                            onChange={(e) => setCourseId(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Enter course ID"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            Default: 51a056c3-b29d-4741-9cb9-5645d7f08b82 (Test Publishing course)
                        </p>
                    </div>

                    {/* Run Tests Button */}
                    <button
                        onClick={runAllTests}
                        disabled={testing || !courseId}
                        className={`w-full py-3 px-6 rounded-lg font-semibold text-white mb-6 transition-all ${
                            testing || !courseId
                                ? 'bg-gray-400 cursor-not-allowed'
                                : 'bg-blue-600 hover:bg-blue-700 shadow-md hover:shadow-lg'
                        }`}
                    >
                        {testing ? '🔄 Running Tests...' : '▶️ Run All Tests'}
                    </button>

                    {/* Results Summary */}
                    {results.length > 0 && (
                        <div className="mb-6 grid grid-cols-3 gap-4">
                            <div className="bg-gray-100 p-4 rounded-lg text-center">
                                <div className="text-2xl font-bold text-gray-700">
                                    {results.length}
                                </div>
                                <div className="text-sm text-gray-600">Total Tests</div>
                            </div>
                            <div className="bg-green-100 p-4 rounded-lg text-center">
                                <div className="text-2xl font-bold text-green-700">
                                    {successCount}
                                </div>
                                <div className="text-sm text-green-600">Passed ✅</div>
                            </div>
                            <div className="bg-red-100 p-4 rounded-lg text-center">
                                <div className="text-2xl font-bold text-red-700">{errorCount}</div>
                                <div className="text-sm text-red-600">Failed ❌</div>
                            </div>
                        </div>
                    )}

                    {/* Test Results */}
                    <div className="space-y-4">
                        {results.map((result, index) => (
                            <div
                                key={index}
                                className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center space-x-3">
                                        <span className="text-2xl">
                                            {getStatusEmoji(result.status)}
                                        </span>
                                        <div>
                                            <span className="font-mono text-sm font-medium text-gray-700">
                                                {result.method}
                                            </span>
                                            <span className="ml-2 text-sm text-gray-600">
                                                {result.endpoint}
                                            </span>
                                        </div>
                                    </div>
                                    {result.duration && (
                                        <span className="text-xs text-gray-500">
                                            {result.duration}ms
                                        </span>
                                    )}
                                </div>

                                {result.status === 'success' && result.response && (
                                    <details className="mt-2">
                                        <summary className="cursor-pointer text-sm text-green-600 hover:text-green-700">
                                            View Response
                                        </summary>
                                        <pre className="mt-2 p-3 bg-gray-50 rounded text-xs overflow-auto max-h-64">
                                            {JSON.stringify(result.response, null, 2)}
                                        </pre>
                                    </details>
                                )}

                                {result.status === 'error' && result.error && (
                                    <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded">
                                        <p className="text-sm text-red-700">
                                            <strong>Error:</strong> {result.error}
                                        </p>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {results.length === 0 && (
                        <div className="text-center py-12 text-gray-400">
                            <p className="text-lg">No tests run yet</p>
                            <p className="text-sm">Click "Run All Tests" to start</p>
                        </div>
                    )}
                </div>

                {/* API Documentation Reference */}
                <div className="mt-6 bg-white rounded-lg shadow p-6">
                    <h2 className="text-xl font-bold mb-4 text-gray-800">📚 API Reference</h2>
                    <div className="space-y-2 text-sm">
                        <div className="grid grid-cols-3 gap-2 text-gray-600 font-semibold border-b pb-2">
                            <div>Endpoint</div>
                            <div>Method</div>
                            <div>Description</div>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-gray-700">
                            <code className="text-xs">/courses/:id/progress</code>
                            <span>GET</span>
                            <span>Get learning progress</span>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-gray-700">
                            <code className="text-xs">/courses/:id/progress</code>
                            <span>POST</span>
                            <span>Update progress</span>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-gray-700">
                            <code className="text-xs">/courses/:id/vocabulary</code>
                            <span>GET</span>
                            <span>List vocabulary</span>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-gray-700">
                            <code className="text-xs">/vocabulary/review-queue</code>
                            <span>GET</span>
                            <span>Spaced repetition queue</span>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-gray-700">
                            <code className="text-xs">/vocabulary/:id/review</code>
                            <span>POST</span>
                            <span>Review word</span>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-gray-700">
                            <code className="text-xs">/notes</code>
                            <span>POST</span>
                            <span>Create note</span>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-gray-700">
                            <code className="text-xs">/courses/:id/notes</code>
                            <span>GET</span>
                            <span>List notes</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LearningAPITestPage;
